import React, { useMemo, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container } from "@pixi/react";

import { SOUND_NAME } from "../../../../sounds";
import { PixiSettings } from "../../../../pixiSettings";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { soundManager } from "../../../../SoundManager";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../pixiSettings";
import { openBonusBuy, closeMenu } from "../../../../State/actions";
import {
  selectBonusBuyActive,
  selectCriticalAnimationPlaying,
  selectHoldAndWinActive,
  selectIsSpinning,
  selectAutoPlayActive,
  selectFreeRoundsActive,
  selectUserInterfaceDisabled,
  selectFreeSpinActive,
  selectRoyalCollectActive,
} from "../../../../State/selectors";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import { safeInit } from "../../../../utilities";
import BuyBonusController from "./BuyBonusController";

// ============================================================================
// BUY BONUS BUTTON CONFIGURATION
// Single source of truth for scale, position per device/orientation
// ============================================================================
const BUY_BONUS_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      scale: 1.2,
      xOffset: 0,
      yOffset: 0,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      scale: 0.75,
      xOffset: 0.35,
      yOffset: -0.4,
    },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      scale: 1.5,
      xOffset: 0,
      yOffset: -1.75,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      scale: 1.0,
      xOffset: 0.45,
      yOffset: -0.5,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      scale: 1.35,
      xOffset: 0.6,
      yOffset: -0.7,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      scale: 1.2,
      xOffset: 0.5,
      yOffset: -0.6,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    scale: 1.25,
    xOffset: 0.5,
    yOffset: -0.6,
  },
};

// Base spine size before device scaling
const BASE_SIZE = 170;

const ButtonBuyBonus = ({ toolbar }) => {
  const {
    device,
    orientation,
    symbolScale,
    symbolWidth,
    symbolHeight,
    dimensions,
    getResponsiveConfig,
    isMobileSmall,
  } = useDimensions();
  const dispatch = useDispatch();
  const popupActive = useSelector(selectBonusBuyActive);
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const isSpinning = useSelector(selectIsSpinning);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  const royalCollectActive = useSelector(selectRoyalCollectActive);

  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const isDisabledRef = useRef(false);
  const popupActiveRef = useRef(false);

  // Resolve device config — use real device for MOBILE_SMALL support
  const deviceConfig = useMemo(() => {
    const realDevice = isMobileSmall
      ? PixiSettings.DEVICE.MOBILE_SMALL
      : device;
    const cfg = BUY_BONUS_CONFIG[realDevice];
    return cfg?.[orientation] ?? cfg ?? { scale: 1, xOffset: 0, yOffset: 0 };
  }, [device, orientation, isMobileSmall]);

  // Spine target size — BASE_SIZE scaled by symbolScale (responsive)
  const targetSize = useMemo(
    () => Math.round(BASE_SIZE * symbolScale),
    [symbolScale],
  );

  // UI button config (same one autoplay uses) — needed for mobile portrait alignment
  const buttonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  const isMobileSmallPortrait =
    isMobileSmall && orientation === PixiSettings.ORIENTATION.PORTRAIT;

  const isMobilePortrait =
    device === PixiSettings.DEVICE.MOBILE &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  // Position calculation
  // Mobile portrait: derive position relative to autoplay button for consistent spacing
  // Other devices: use symbolWidth/symbolHeight multipliers from config
  const buttonPosition = useMemo(() => {
    if (isMobileSmallPortrait) {
      const autoplayY = -dimensions.height / 9.5;
      return {
        x: buttonConfig.width,
        y: autoplayY - dimensions.height / 16.5,
      };
    }
    if (isMobilePortrait) {
      // Same X as autoplay: toolbar.width / 2 - buttonConfig.width * 1.7
      // Y: autoplay is at -dimensions.height / 11, place buy bonus above it
      // separated by dimensions.height / 11 (same fraction = equal spacing)
      const autoplayY = -dimensions.height / 9.5;
      return {
        x: buttonConfig.width,
        y: autoplayY - dimensions.height / 15,
      };
    }
    return {
      x: (deviceConfig.xOffset || 0) * symbolWidth,
      y: (deviceConfig.yOffset || 0) * symbolHeight,
    };
  }, [
    isMobileSmallPortrait,
    isMobilePortrait,
    deviceConfig,
    symbolWidth,
    symbolHeight,
    buttonConfig,
    dimensions,
  ]);

  // Disabled conditions
  const isDisabled =
    userInterfaceDisabled ||
    criticalAnimationPlaying ||
    holdAndWinActive ||
    isSpinning ||
    autoPlayActive ||
    freeSpinActive ||
    royalCollectActive ||
    (FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive);

  // Keep refs in sync for use in callbacks
  useEffect(() => {
    isDisabledRef.current = isDisabled;
  }, [isDisabled]);

  useEffect(() => {
    popupActiveRef.current = popupActive;
  }, [popupActive]);

  // Initialize spine controller
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      safeInit(() => {
        controllerRef.current = new BuyBonusController(
          containerRef.current,
          targetSize,
          targetSize,
          "BUY BONUS",
          deviceConfig,
          {
            onTap: () => {
              if (popupActiveRef.current || isDisabledRef.current) return;
              soundManager.play(SOUND_NAME.button_click);
              controllerRef.current?.playClick(() => {
                dispatch(closeMenu());
                dispatch(openBonusBuy());
              });
            },
            onHover: () => {
              if (isDisabledRef.current) return;
              controllerRef.current?.playHover();
            },
            onOut: () => {
              controllerRef.current?.returnToIdle();
            },
          },
        );
      });
    }

    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
        controllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update dimensions on resize
  useEffect(() => {
    if (controllerRef.current?.isInitialized) {
      controllerRef.current.updateDimensions(
        targetSize,
        targetSize,
        deviceConfig,
      );
    }
  }, [targetSize, deviceConfig]);

  // Update disabled state
  useEffect(() => {
    if (controllerRef.current?.isInitialized) {
      controllerRef.current.setDisabled(isDisabled);
    }
  }, [isDisabled]);

  return (
    <Container
      name="primary_button_buy_bonus"
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={2}
      visible={!freeSpinActive && !royalCollectActive}
    >
      <Container
        ref={containerRef}
        sortableChildren={true}
        name="buy_bonus_spine_container"
      />
    </Container>
  );
};

export default ButtonBuyBonus;
